/**
 * Card Agenda Component
 * Gère les interactions des cartes d'agenda avec fonctionnalité "voir plus"
 */

class CardAgenda {
  private modal: HTMLElement | null = null;
  private modalText: HTMLElement | null = null;
  private modalTitle: HTMLElement | null = null;
  private activeCalendarMenu: HTMLElement | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    this.setupModal();
    this.checkTextOverflow();
    this.bindEvents();
  }

  private setupModal(): void {
    this.modal = document.getElementById('agenda-modal-full');
    this.modalText =
      this.modal?.querySelector('.card-agenda__modal-text') || null;
    this.modalTitle =
      this.modal?.querySelector('.card-agenda__modal-title') || null;
  }

  private checkTextOverflow(): void {
    const excerptElements = document.querySelectorAll('.card-agenda__excerpt');

    excerptElements.forEach((excerpt) => {
      const element = excerpt as HTMLElement;
      const seeMoreButton = element.parentElement?.querySelector(
        '.card-agenda__see-more'
      ) as HTMLElement;

      if (seeMoreButton) {
        const textContent = element.textContent || '';

        // Vérifier différents types d'ellipses WordPress
        const hasWordPressEllipsis =
          textContent.endsWith('...') ||
          textContent.endsWith('…') ||
          textContent.includes('[...]') ||
          textContent.length > 150; // Fallback pour texte long (plus long pour card-agenda)

        if (hasWordPressEllipsis) {
          seeMoreButton.style.display = 'inline';
        } else {
          seeMoreButton.style.display = 'none';
        }
      }
    });
  }

  private bindEvents(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const seeMoreButton = target.closest(
        '[data-action="see-more"]'
      ) as HTMLElement;
      if (seeMoreButton) {
        e.preventDefault();
        this.handleSeeMoreClick(seeMoreButton);
        return;
      }

      const addCalendarButton = target.closest(
        '[data-action="add-calendar"]'
      ) as HTMLElement;
      if (addCalendarButton) {
        e.preventDefault();
        this.handleAddCalendarClick(addCalendarButton);
        return;
      }

      const calendarOption = target.closest('[data-calendar]') as HTMLElement;
      if (calendarOption) {
        e.preventDefault();
        this.handleCalendarOptionClick(calendarOption);
        return;
      }

      const closeButton = target.closest(
        '[data-action="close-modal"]'
      ) as HTMLElement;
      if (closeButton) {
        e.preventDefault();
        this.closeModal();
        return;
      }

      // Fermer le modal en cliquant sur l'overlay
      if (target.classList.contains('card-agenda__modal-overlay')) {
        this.closeModal();
        return;
      }

      // Fermer le menu calendrier en cliquant ailleurs
      if (!target.closest('.card-agenda__actions')) {
        this.closeCalendarMenus();
      }
    });

    // Fermer le modal avec la touche Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.modal?.style.display === 'block') {
          this.closeModal();
        } else {
          this.closeCalendarMenus();
        }
      }
    });
  }

  private handleSeeMoreClick(button: HTMLElement): void {
    const fullText = button.getAttribute('data-full-text');
    const cardElement = button.closest('.card-agenda');
    const titleElement = cardElement?.querySelector(
      '.card-agenda__title'
    ) as HTMLElement;

    if (fullText && this.modal && this.modalText && this.modalTitle) {
      this.modalText.innerHTML = fullText;
      this.modalTitle.innerHTML =
        titleElement?.textContent || "Détails de l'événement";
      this.openModal();
    }
  }

  private openModal(): void {
    if (this.modal) {
      this.modal.style.display = 'block';
      document.body.style.overflow = 'hidden'; // Empêcher le scroll de la page
    }
  }

  private closeModal(): void {
    if (this.modal) {
      this.modal.style.display = 'none';
      document.body.style.overflow = 'auto'; // Restaurer le scroll de la page
    }
  }

  private handleAddCalendarClick(button: HTMLElement): void {
    const cardElement = button.closest('.card-agenda');
    const menu = cardElement?.querySelector(
      '.card-agenda__calendar-menu'
    ) as HTMLElement;

    if (menu) {
      // Fermer les autres menus ouverts
      this.closeCalendarMenus();

      // Ouvrir ce menu
      menu.style.display = 'block';
      this.activeCalendarMenu = menu;
    }
  }

  private handleCalendarOptionClick(option: HTMLElement): void {
    const calendarType = option.getAttribute('data-calendar');
    const cardElement = option.closest('.card-agenda');
    const addButton = cardElement?.querySelector(
      '[data-action="add-calendar"]'
    ) as HTMLElement;

    if (calendarType && addButton) {
      const eventData = this.extractEventData(addButton);
      const calendarUrl = this.generateCalendarUrl(calendarType, eventData);

      if (calendarUrl) {
        window.open(calendarUrl, '_blank', 'noopener,noreferrer');
      }

      this.closeCalendarMenus();
    }
  }

  private extractEventData(button: HTMLElement): EventData {
    return {
      title: button.getAttribute('data-title') || '',
      date: button.getAttribute('data-date') || '',
      startTime: button.getAttribute('data-start-time') || '',
      endTime: button.getAttribute('data-end-time') || '',
      description: button.getAttribute('data-description') || '',
      location: button.getAttribute('data-location') || ''
    };
  }

  private generateCalendarUrl(
    calendarType: string,
    eventData: EventData
  ): string | null {
    try {
      // Validation : vérifier que la date est fournie
      if (!eventData.date || eventData.date.trim() === '') {
        console.error("Date de l'événement manquante");
        return null;
      }

      // Validation : vérifier que la date est valide
      const dateObj = new Date(eventData.date);
      if (isNaN(dateObj.getTime())) {
        console.error('Date invalide:', eventData.date);
        return null;
      }

      if (calendarType === 'google') {
        return this.generateGoogleCalendarUrl(eventData);
      } else if (calendarType === 'teams') {
        return this.generateMicrosoftCalendarUrl(eventData, 'office');
      } else if (calendarType === 'teams-perso') {
        return this.generateMicrosoftCalendarUrl(eventData, 'live');
      } else if (calendarType === 'apple') {
        this.generateAndDownloadICS(eventData);
      }
    } catch (error) {
      console.error('Erreur lors de la génération du lien calendrier:', error);
    }
    return null;
  }

  /**
   * Génère et télécharge un fichier ICS pour calendrier Apple/Outlook.
   * Supporte les événements avec ou sans heures.
   * @param eventData Les données de l'événement.
   */
  private generateAndDownloadICS(eventData: EventData): void {
    let dtstart: string;
    let dtend: string;

    // Si on a des heures, utiliser le format avec heures
    if (eventData.startTime && eventData.endTime) {
      const startDateTime = new Date(
        `${eventData.date}T${eventData.startTime}`
      );
      const endDateTime = new Date(`${eventData.date}T${eventData.endTime}`);

      dtstart = `DTSTART:${this._formatICSDateTime(startDateTime)}`;
      dtend = `DTEND:${this._formatICSDateTime(endDateTime)}`;
    } else if (eventData.startTime) {
      // Si seulement heure de début, durée d'1 heure par défaut
      const startDateTime = new Date(
        `${eventData.date}T${eventData.startTime}`
      );
      const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

      dtstart = `DTSTART:${this._formatICSDateTime(startDateTime)}`;
      dtend = `DTEND:${this._formatICSDateTime(endDateTime)}`;
    } else {
      // Événement toute la journée
      const startDate = new Date(eventData.date);
      const endDate = new Date(eventData.date);
      endDate.setDate(endDate.getDate() + 1);

      dtstart = `DTSTART;VALUE=DATE:${this._formatICSDateOnly(startDate)}`;
      dtend = `DTEND;VALUE=DATE:${this._formatICSDateOnly(endDate)}`;
    }

  const cleanedDescription = this._cleanHTMLContent(eventData.description ?? '');
  const CRLF = '\r\n';

  const icsContent =
  'BEGIN:VCALENDAR' + CRLF +
  'VERSION:2.0' + CRLF +
  'PRODID:-//Cotelub//Evenements//EN' + CRLF +
  'BEGIN:VEVENT' + CRLF +
  `UID:${Date.now()}@intranet.cotelub.fr` + CRLF +
  `DTSTAMP:${this._formatICSDateTime(new Date())}` + CRLF +
  `${dtstart}` + CRLF +
  `${dtend}` + CRLF +
  `SUMMARY:${this._escapeICSString(eventData.title)}` + CRLF +
  `LOCATION:${this._escapeICSString(eventData.location ?? '')}` + CRLF +
  `DESCRIPTION:${this._escapeICSString(cleanedDescription)}` + CRLF +
  'END:VEVENT' + CRLF +
  'END:VCALENDAR';


    // Le reste du code déclenche le téléchargement du fichier .ics
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    const fileName = `${eventData.title
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()}.ics`;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

    /**
   * Nettoie une chaîne de contenu riche (HTML) en texte brut.
   * - Supprime les balises HTML (strip_tags).
   * - Décode les entités HTML (html_entity_decode).
   * @param htmlContent Le contenu HTML de l'éditeur WYSIWYG.
   */
    private _cleanHTMLContent(htmlContent: string): string {
        if (!htmlContent) return '';

        // Utilise le DOM du navigateur pour le décodage et la suppression de balises HTML.
        // Cette méthode est plus sûre et plus complète que les RegEx simples.
        const tempElement = document.createElement('div');
        
        // 1. Décodage des entités HTML (Ex: &agrave; -> à).
        // Le fait d'assigner le HTML à innerHTML force le décodage.
        tempElement.innerHTML = htmlContent;
        
        // 2. Récupérer le texte brut (strip_tags).
        // textContent récupère le texte sans les balises HTML.
        let plainText = tempElement.textContent || tempElement.innerText || '';

        // Optionnel : Nettoyer les sauts de ligne excessifs (si votre WYSIWYG
        // a remplacé <p> par plusieurs \n lors de la récupération de textContent)
        plainText = plainText.replace(/(\r\n|\n|\r){3,}/g, '\r\n\r\n');

        return plainText.trim();
    }

  
  private _escapeICSString(str: string): string {
    return str.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  }

  private _formatICSDateOnly(date: Date): string {
    // Format YYYYMMDD pour les événements toute la journée
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('');
  }

  private _formatICSDateTime(date: Date): string {
    // Format YYYYMMDDTHHMMSS pour les événements avec heure
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
      'T',
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0'),
      String(date.getSeconds()).padStart(2, '0')
    ].join('');
  }
  /**
   * Génère un lien de composition d'événement Outlook (Deep Link).
   * Supporte les événements avec ou sans heures.
   * @param eventData Les données de l'événement.
   * @param type Type de compte Outlook ('office' ou 'live').
   * @returns L'URL Outlook/Microsoft.
   */
  private generateMicrosoftCalendarUrl(
    eventData: EventData,
    type: 'office' | 'live'
  ): string {
    const params: any = {
      subject: eventData.title,
      body: eventData.description ?? '',
      location: eventData.location ?? '',
      path: '/calendar/action/compose'
    };

    // Si on a des heures, utiliser le format avec heures
    if (eventData.startTime && eventData.endTime) {
      const startDateTime = `${eventData.date}T${eventData.startTime}`;
      const endDateTime = `${eventData.date}T${eventData.endTime}`;

      params.startdt = startDateTime;
      params.enddt = endDateTime;
    } else if (eventData.startTime) {
      // Si seulement heure de début, durée d'1 heure par défaut
      const startDateTime = new Date(
        `${eventData.date}T${eventData.startTime}`
      );
      const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

      params.startdt = `${eventData.date}T${eventData.startTime}`;
      params.enddt = `${eventData.date}T${String(endDateTime.getHours()).padStart(2, '0')}:${String(endDateTime.getMinutes()).padStart(2, '0')}`;
    } else {
      // Événement toute la journée
      const start = eventData.date;
      const endDate = new Date(eventData.date);
      endDate.setDate(endDate.getDate() + 1);
      const end = endDate.toISOString().slice(0, 10);

      params.startdt = start;
      params.enddt = end;
      params.allday = 'true';
    }

    const urlParams = new URLSearchParams(params);
    return `https://outlook.${type}.com/calendar/action/compose?${urlParams.toString()}`;
  }

  private generateGoogleCalendarUrl(eventData: EventData): string {
    let datesParam: string;

    // Si on a des heures de début et fin, utiliser le format avec heures
    if (eventData.startTime && eventData.endTime) {
      // Combiner date + heure pour créer un timestamp
      const startDateTime = new Date(
        `${eventData.date}T${eventData.startTime}`
      );
      const endDateTime = new Date(`${eventData.date}T${eventData.endTime}`);

      // Format Google Calendar avec heure : YYYYMMDDTHHmmss
      const startFormatted = this._formatGoogleDateTime(startDateTime);
      const endFormatted = this._formatGoogleDateTime(endDateTime);

      datesParam = `${startFormatted}/${endFormatted}`;
    } else if (eventData.startTime) {
      // Si seulement heure de début, durée d'1 heure par défaut
      const startDateTime = new Date(
        `${eventData.date}T${eventData.startTime}`
      );
      const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000); // +1 heure

      const startFormatted = this._formatGoogleDateTime(startDateTime);
      const endFormatted = this._formatGoogleDateTime(endDateTime);

      datesParam = `${startFormatted}/${endFormatted}`;
    } else {
      // Événement toute la journée : format YYYYMMDD/YYYYMMDD
      const startDate = eventData.date.replace(/-/g, '');
      const endDateObj = new Date(eventData.date);
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDate = endDateObj.toISOString().slice(0, 10).replace(/-/g, '');

      datesParam = `${startDate}/${endDate}`;
    }

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventData.title,
      dates: datesParam,
      location: eventData.location ?? '',
      details: eventData.description ?? ''
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  private _formatGoogleDateTime(date: Date): string {
    // Format : YYYYMMDDTHHmmss
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
      'T',
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0'),
      String(date.getSeconds()).padStart(2, '0')
    ].join('');
  }

  private closeCalendarMenus(): void {
    const menus = document.querySelectorAll('.card-agenda__calendar-menu');
    menus.forEach((menu) => {
      (menu as HTMLElement).style.display = 'none';
    });
    this.activeCalendarMenu = null;
  }
}

interface EventData {
  title: string;
  date: string; // Date de l'événement (YYYY-MM-DD)
  startTime?: string; // Heure de début (HH:mm)
  endTime?: string; // Heure de fin (HH:mm)
  description?: string;
  location?: string;
}

// Initialiser quand le DOM est prêt
document.addEventListener('DOMContentLoaded', () => {
  new CardAgenda();
});

// Export pour utilisation externe si nécessaire
export default CardAgenda;
